import { EventTimeline, EventType, MatrixClient, Room } from 'matrix-js-sdk/src/matrix';

import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import useIsRoomInCurrentWorkspace from 'hooks/useIsRoomInCurrentWorkspace';
import useScopedUserPowerLevels from 'hooks/useScopedUserPowerLevels/useScopedUserPowerLevels';
import { meetsChatPermissionThreshold } from 'utils/chatPermissionGates';
import { CustomMeedioMatrixEvents, getUserPowerLevel } from 'utils/matrixUtils';
import { PermissionId, workspacesConfig } from 'utils/powerLevelsConfig';
import { getCanonicalParentRoom } from 'utils/roomHierarchyUtils';
import { readRoomManagerIds } from 'utils/roomManagers';

export type ChatMeetingCreatePermissions = {
  canCreateInstantCall: boolean;
  canScheduleMeeting: boolean;
  canCreateAnyMeeting: boolean;
};

type ComputeChatMeetingCreatePermissionsParams = {
  scopedChatPl: number;
  inRoomPl: number;
  isRoomManager: boolean;
  /** True when the user is a member of the room's workspace (room in current workspace). */
  isInWorkspace: boolean;
  /** Required PL to send the instant-call event in THIS room (from m.room.power_levels). */
  roomRequiredInstantCallPl: number;
  /** Required PL to send the scheduled-meeting event in THIS room (from m.room.power_levels). */
  roomRequiredScheduleMeetingPl: number;
};

/** Required PL to send `eventType` in the room — from m.room.power_levels (falls back to events_default, else 0). */
export const getRoomEventRequiredPowerLevel = (room: Room | null | undefined, eventType: string): number => {
  const content = room
    ?.getLiveTimeline?.()
    ?.getState?.(EventTimeline.FORWARDS)
    ?.getStateEvents?.(EventType.RoomPowerLevels, '')
    ?.getContent?.();
  if (!content) return 0;
  const events = (content.events ?? {}) as Record<string, number>;
  if (typeof events[eventType] === 'number') return events[eventType];
  return (content.events_default as number) ?? 0;
};

export const computeChatMeetingCreatePermissions = ({
  scopedChatPl,
  inRoomPl,
  isRoomManager,
  isInWorkspace,
  roomRequiredInstantCallPl,
  roomRequiredScheduleMeetingPl,
}: ComputeChatMeetingCreatePermissionsParams): ChatMeetingCreatePermissions => {
  const instantCallThreshold = workspacesConfig[PermissionId.ChatCreateInstantCall].powerLevel;
  const scheduleMeetingThreshold = workspacesConfig[PermissionId.ChatScheduleMeeting].powerLevel;

  // BUG-004: workspace members keep the scope/in-room/manager gate. Users who are NOT members of the
  // room's workspace (e.g. guests who joined a shared-access room) must satisfy the room's actual
  // power-level requirement for the meeting-creation event — the workspace's default member-role PL
  // must not leak to them via `scopedChatPl`. Personal rooms (no workspace parent) are non-workspace
  // with a required PL of 0, so any joined user still passes.
  const canCreateInstantCall = isInWorkspace
    ? meetsChatPermissionThreshold(scopedChatPl, inRoomPl, isRoomManager, instantCallThreshold)
    : inRoomPl >= roomRequiredInstantCallPl;
  const canScheduleMeeting = isInWorkspace
    ? meetsChatPermissionThreshold(scopedChatPl, inRoomPl, isRoomManager, scheduleMeetingThreshold)
    : inRoomPl >= roomRequiredScheduleMeetingPl;

  return {
    canCreateInstantCall,
    canScheduleMeeting,
    canCreateAnyMeeting: canCreateInstantCall || canScheduleMeeting,
  };
};

export const getChatMeetingCreatePermissionsForMessengerRoom = (
  matrixClient: MatrixClient,
  messengerRoomId: string,
  scopedChatPl: number,
  isInWorkspace: boolean
): ChatMeetingCreatePermissions => {
  const messengerRoom = matrixClient.getRoom(messengerRoomId);
  const currentUserId = matrixClient.getUserId() ?? '';
  const inRoomPL = messengerRoom && currentUserId ? getUserPowerLevel(messengerRoom, currentUserId) : 0;
  const isRoomManager = !!(currentUserId && messengerRoom && readRoomManagerIds(messengerRoom)?.has(currentUserId));

  return computeChatMeetingCreatePermissions({
    scopedChatPl,
    inRoomPl: inRoomPL,
    isRoomManager,
    isInWorkspace,
    roomRequiredInstantCallPl: getRoomEventRequiredPowerLevel(messengerRoom, CustomMeedioMatrixEvents.MESSENGER_CALL),
    roomRequiredScheduleMeetingPl: getRoomEventRequiredPowerLevel(
      messengerRoom,
      CustomMeedioMatrixEvents.SCHEDULED_MEETING
    ),
  });
};

const useChatMeetingCreatePermissions = (messengerRoomId: string): ChatMeetingCreatePermissions => {
  const { matrixClient } = useMatrixContext();
  const { getIsRoomInCurrentWorkspace } = useIsRoomInCurrentWorkspace();
  const messengerRoom = matrixClient.getRoom(messengerRoomId);
  const workspaceRoomId = messengerRoom ? getCanonicalParentRoom(messengerRoom)?.roomId ?? null : null;
  const scopedPowerLevels = useScopedUserPowerLevels(workspaceRoomId);

  return getChatMeetingCreatePermissionsForMessengerRoom(
    matrixClient,
    messengerRoomId,
    scopedPowerLevels.chat,
    getIsRoomInCurrentWorkspace(messengerRoomId)
  );
};

export default useChatMeetingCreatePermissions;
